/**
 * Copyright (C) 2021 Prosperia Salud, S.A. DE C.V. info@prosperia.health.
 * All rights reserved
 * DO NOT REDISTRIBUTE
 * The software MAY NOT be rewritten or refactored by any means. The code MAY
 * NOT be packaged and redistributed by any means without prior written
 * permission from the author.
 */
import { useContext } from 'react';
/// /// EXTERNAL /// ///
// third party libraries //
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
/// /// INTERNAL /// ///
// context //
import { ErrorContext } from 'context/ErrorContext';
// utils //
import { ErrorStatus } from 'utils/constants';

const METHOD_POST = 'post';
const METHOD_GET = 'get';
const APIS_SAFELY = Object.keys(process.env).filter((key) => key.includes('SECURITY'))
  .map((key) => process.env[key]);

const findUrl = (url) => {
  const find = APIS_SAFELY.filter((api) => api.includes(url));
  return find.length > 0;
};

const transformPayload = (request) => {
  const { method, url } = request;
  if (findUrl(url)) {
    const sessionId = Cookies.get('session_id');
    if (method === METHOD_GET) {
      request.params = {
        ...request.params,
        ...(sessionId && {
          session_id: sessionId,
        }),
      };
    } else if (method === METHOD_POST) {
      const requestPayload = request.data.data;
      if (requestPayload) {
        request.data = {
          ...request.data,
          data: {
            ...request.data.data,
            ...(sessionId && {
              session_id: sessionId,
            }),
          },
        };
      } else {
        request.data = {
          ...request.data,
          data: {
            ...(sessionId && {
              session_id: sessionId,
            }),
          },
        };
      }
    }
  }
};
const Interceptors = () => {
  const navigate = useNavigate();
  const { setMessage } = useContext(ErrorContext);

  axios.interceptors.request.use(
    async (request) => {
      const token = Cookies.get('token');
      if (token) request.headers.Authorization = `Bearer ${token}`;
      transformPayload(request);
      return request;
    },
    (error) => {
      if (error?.response?.data?.message) {
        setMessage({
          open: true,
          severity: 'error',
          text: error?.response?.data?.message,
        });
      }
      const errorInfo = {
        method: error.config.method,
        url: error.config.url,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.config.headers,
      };

      Sentry.withScope((scope) => {
        scope.setTag('axios_error', 'true');
        scope.setContext('axios_error_details_request', errorInfo);
        Sentry.captureException(error);
      });
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = error.response.config;
      let payloadRequest = null;
      if (originalRequest.data) {
        payloadRequest = JSON.parse(originalRequest.data);
      }
      if (!payloadRequest?.data?.skipErrorMessage) {
        if (error.response) {
          if (error.response?.status) {
            const { status } = error.response;
            if (status === ErrorStatus.NOT_FOUND
              || status === ErrorStatus.NOT_AUTHORIZED
              || status === ErrorStatus.FORBIDDEN_ERROR
              || status === ErrorStatus.LEGAL_REASON_UNAVAILABLE) {
              navigate(`/error/${status}`);
            } else {
              let message = error.response?.data?.message;
              const type = error.response?.data?.severity;
              if (message) {
                setMessage({
                  open: true,
                  severity: type || 'error',
                  text: message,
                });
              } else {
                message = `Lo sentimos, pero no podemos procesar su solicitud en este momento. 
                Póngase en contacto con el equipo de soporte.`;
                if (status === ErrorStatus.GATEWAY_TIMEOUT
                  || status === ErrorStatus.NETWORK_ERROR) {
                  message = `Lo sentimos, estamos experimentando retrasos en el procesamiento 
                  de su solicitud. Póngase en contacto con el equipo de soporte.`;
                }
                setMessage({
                  open: true,
                  severity: type || 'error',
                  text: message,
                });
              }
            }
          }
        } else {
          const { message } = error;
          // canceled by AbortController
          if (!message || message !== 'canceled') {
            setMessage({
              open: true,
              severity: 'error',
              text: 'Lo sentimos, pero no podemos procesar su solicitud en este momento.',
            });
          }
        }
      }

      const errorInfo = {
        method: error.config.method,
        url: error.config.url,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        payloadRequest: payloadRequest?.data,
        headers: error.config.headers,
      };

      Sentry.withScope((scope) => {
        scope.setTag('axios_error', 'true');
        scope.setContext('axios_error_details_response', errorInfo);
        Sentry.captureException(error);
      });
      return Promise.reject(error);
    },
  );

  return null;
};

export default Interceptors;
